import { Alerts } from "../../../../utils/Alerts.js";
import { useRPCContext } from "../../../../contexts/RPCContext.js";
import { FormikTextarea } from "../../../../components/FormikFields/FormikTextarea.js";
import { FormikFilesPicker } from "../../../Files/shared/FormikFilesPicker.js";
import { FormikDateTimePicker } from "../../../../components/FormikFields/FormikDateTimePicker.js";
import { FormikSelectPriority } from "../../../../components/Priority/FormikSelectPriority.js";
import { FormikText } from "../../../../components/FormikFields/FormikText.js";
import { useBolo } from "../../../Bolos/hooks/useBolo.js";
import { RefsByTypeForm } from "../../../shared/RefsByTypeForm/index.js";
import { SelectLocationAndGroup } from "../SelectLocationAndGroup.js";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { FormLayout, Icon, Tabs } from "@therms/atalaya";
import { IoWarning } from "react-icons/io5";
import { jsx, jsxs } from "react/jsx-runtime";
import { Formik } from "formik";
import * as Yup from "yup";
//#region src/modules/Locations/shared/Bolo/LocationBoloForm.tsx
var ValidationSchema = Yup.object().shape({
	activeDate: Yup.string().nullable(),
	body: Yup.string().required(),
	expireDate: Yup.string().required(),
	fileUrls: Yup.array(Yup.string()).nullable(),
	primaryImageUrl: Yup.string().nullable(),
	priority: Yup.number().nullable(),
	regionIds: Yup.array(Yup.string()).nullable(),
	title: Yup.string().required()
});
var initialState = {
	activeDate: (/* @__PURE__ */ new Date()).toISOString(),
	body: "",
	expireDate: void 0,
	fileUrls: [],
	organizationId: void 0,
	primaryImageUrl: void 0,
	priority: 0,
	regionIds: [],
	title: ""
};
var expireDate = dayjs().add(2, "weeks").toISOString();
function LocationBoloForm({ boloId, isPortal, locationId, locationIdFilter, onCancel, onComplete }) {
	const { rpcService } = useRPCContext();
	const [tab, setTab] = useState("details");
	const [locationAndGroupIds, setLocationAndGroupIds] = useState([locationId]);
	const { data, loading } = useBolo(boloId ? { boloId } : void 0);
	useEffect(() => {
		if (!data) return;
		if (data?.bolo?.locationGroupIds?.length && data?.bolo?.locationIds?.length) return setLocationAndGroupIds([...data.bolo.locationIds, ...data.bolo.locationGroupIds]);
		if (data?.bolo?.locationIds?.length) return setLocationAndGroupIds(data.bolo.locationIds);
		if (data?.bolo?.locationGroupIds?.length) return setLocationAndGroupIds(data.bolo.locationGroupIds);
	}, [data]);
	const save = useCallback(async (values) => {
		try {
			if ("id" in values) await rpcService.Locations.UpdateLocationBolo.v1.call({
				bolo: values,
				locationAndGroupIds
			});
			else await rpcService.Locations.CreateLocationBolo.v1.call({
				bolo: values,
				isPortalBolo: isPortal,
				locationAndGroupIds
			});
			onComplete();
			Alerts.success(`Bolo saved`);
		} catch (err) {
			Alerts.error("There was a problem saving the BOLO", err.message);
			console.log("save() err", err);
		}
	}, [
		isPortal,
		locationAndGroupIds,
		onComplete
	]);
	const handleDelete = useCallback(async () => {
		if (!boloId) return;
		try {
			await rpcService.Bolos.DeleteBolo.v1.call({ boloId });
			onComplete();
		} catch (err) {
			console.log("handleDelete() err", err);
		}
	}, [boloId, onComplete]);
	return /* @__PURE__ */ jsx(Formik, {
		initialValues: {
			...initialState,
			expireDate,
			...data?.bolo
		},
		onSubmit: save,
		enableReinitialize: true,
		validationSchema: ValidationSchema,
		children: ({ errors, isSubmitting, submitCount, submitForm }) => /* @__PURE__ */ jsxs(FormLayout, {
			space: "lg",
			loading: isSubmitting || loading,
			loadingMessage: loading ? "Loading Bolo" : "Saving Bolo",
			relative: false,
			children: [/* @__PURE__ */ jsxs(Tabs, {
				currentKey: tab,
				onTabClick: setTab,
				children: [/* @__PURE__ */ jsxs(Tabs.Tab, {
					tabKey: "details",
					title: /* @__PURE__ */ jsxs("span", {
						className: "space-x-xs",
						children: [/* @__PURE__ */ jsx("span", { children: "Details" }), errors && !!submitCount && tab !== "details" && /* @__PURE__ */ jsx(Icon, {
							Icon: IoWarning,
							color: "critical",
							size: "sm"
						})]
					}),
					children: [
						/* @__PURE__ */ jsx(FormikText, {
							hint: "required",
							label: "Title",
							name: "title",
							placeholder: "Dangerous Individual",
							required: true
						}),
						/* @__PURE__ */ jsx(FormikTextarea, {
							hint: "required",
							label: "Description",
							name: "body",
							placeholder: "BOLO details...",
							required: true
						}),
						/* @__PURE__ */ jsx(SelectLocationAndGroup, {
							combineSelected: true,
							hideSelectedOptions: false,
							idFilter: locationIdFilter,
							isClearable: true,
							label: "Location and Group",
							multi: true,
							name: "locationAndGroupIds",
							onChangeValue: setLocationAndGroupIds,
							placeholder: "Select Location and Group",
							value: locationAndGroupIds
						}),
						/* @__PURE__ */ jsxs(FormLayout.Section, {
							dividers: "space",
							space: "sm",
							subTitle: "A bolo will only be active between these dates. You can set the From date to some time in the future to keep it inactive until then.",
							children: [/* @__PURE__ */ jsx(FormikDateTimePicker, {
								label: "From",
								mode: "date",
								name: "activeDate"
							}), /* @__PURE__ */ jsx(FormikDateTimePicker, {
								hint: "required",
								label: "Until",
								mode: "date",
								name: "expireDate",
								required: true
							})]
						}),
						/* @__PURE__ */ jsx(FormikSelectPriority, {
							name: "priority",
							label: "Priority"
						}),
						/* @__PURE__ */ jsx(FormikFilesPicker, { name: "fileUrls" })
					]
				}), /* @__PURE__ */ jsx(Tabs.Tab, {
					tabKey: "refs",
					title: "Refs",
					children: /* @__PURE__ */ jsx(RefsByTypeForm, {
						controller: "Bolo",
						data: data?.bolo?.refsByType
					})
				})]
			}), /* @__PURE__ */ jsx(FormLayout.Controls, {
				onCancel,
				onDelete: boloId ? handleDelete : void 0,
				onSubmit: () => {
					submitForm();
				},
				submitting: isSubmitting && "Saving Bolo"
			})]
		})
	});
}
//#endregion
export { LocationBoloForm };

//# sourceMappingURL=LocationBoloForm.js.map